/**
 * domains/ai/thesis2/pipeline.ts — MER Algo Footprints v7.1H
 *
 * AI Thesis Engine — main pipeline.
 *
 * Type discipline:
 *   Once the raw AI response passes validateRawAIResponse(), we work with a
 *   strongly typed RawAIThesis object (z.infer<typeof RawAIThesisSchema>).
 *   No Record<string,unknown> or unsafe casts after that point.
 *   assembleThesis() takes RawAIThesis — direct field access, no wrappers.
 *
 * Pipeline steps:
 *   1.  evaluateDeterministicGates
 *   2.  scoreConfidence (gate-aware)
 *   3.  buildNormalizedPayload + hash
 *   4.  buildAIMessages (system + user separated)
 *   5.  aiCaller (tier 1)
 *   6.  parseAIJSON
 *   7.  validateRawAIResponse → RawAIThesis (typed)
 *   8a. repair: real model repair call if validation failed
 *   8b. repair: deterministic repairFields if model repair failed
 *   9.  assembleThesis (typed RawAIThesis → StructuredThesis)
 *  10.  enforceGates (downgrade state + posture if needed)
 *  11.  detectThesisChanges
 *  12.  validateFinalThesis (Zod — final integrity check)
 *  13.  buildFallbackThesis on any unrecoverable failure
 */

import { nanoid }      from 'nanoid';
import { APP_VERSION } from '../../../config/version';
import {
  StructuredThesis, ThesisEvidenceInput, GateConfig, DEFAULT_GATE_CONFIG,
  ValidationStatus,
} from './types';
import { evaluateDeterministicGates, scoreConfidence } from './gates';
import { buildNormalizedPayload }                      from './evidence';
import { buildAIMessages, buildRepairMessages, PROMPT_VERSION } from './prompt';
import {
  validateRawAIResponse, validateFinalThesis, RawAIThesis, SCHEMA_VERSION,
} from './schema';
import {
  parseAIJSON, repairFields, detectThesisChanges, validatePriceField,
} from './validator';
import { buildFallbackThesis } from './fallback';

// ─── djb2 hash — deterministic fingerprint of normalized evidence payload ─────
function hashEvidence(s: string): string {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
  return (h >>> 0).toString(16).padStart(8, '0');
}

// ─── Types ────────────────────────────────────────────────────────────────────

export type AICaller = (
  system:   string,
  messages: Array<{ role: 'user' | 'assistant'; content: string }>,
) => Promise<{ text: string; modelId: string }>;

export interface ThesisPipelineResult {
  thesis:            StructuredThesis;
  validation_status: ValidationStatus;
  gate_passed:       boolean;
  latency_ms:        number;
}

// ─── Gate enforcement ─────────────────────────────────────────────────────────

function enforceGates(
  thesis_state:   StructuredThesis['thesis_state'],
  action_posture: StructuredThesis['action_posture'],
  gates:          ReturnType<typeof evaluateDeterministicGates>,
  status:         ValidationStatus,
): {
  thesis_state:   StructuredThesis['thesis_state'];
  action_posture: StructuredThesis['action_posture'];
  status:         ValidationStatus;
} {
  if (!gates.passed && thesis_state === 'ACTIONABLE') {
    return {
      thesis_state:   gates.downgraded_to  ?? 'DEVELOPING',
      action_posture: gates.forced_posture ?? 'NEUTRAL',
      status:         'GATE_DOWNGRADED',
    };
  }
  if (!gates.passed && gates.forced_posture) {
    return {
      thesis_state,
      action_posture: gates.forced_posture,
      status:         status === 'AI_VALIDATED' ? 'GATE_DOWNGRADED' : status,
    };
  }
  return { thesis_state, action_posture, status };
}

// ─── Assembly — typed RawAIThesis → StructuredThesis ─────────────────────────
//
// Takes a fully validated, typed RawAIThesis. No generic object access.
// Price fields are sanity-checked against current market price.
// Engine values (stop_quality, risk_reward, trap_conflict) always come from
// input.risk — never from AI.

function assembleThesis(
  typed:          RawAIThesis,
  input:          ThesisEvidenceInput,
  confidence:     ReturnType<typeof scoreConfidence>,
  gates:          ReturnType<typeof evaluateDeterministicGates>,
  status:         ValidationStatus,
  latencyMs:      number,
  evidenceHash:   string,
  modelId:        string,
  repairAttempts: number,
): StructuredThesis {
  const thesis_id = `ai-${nanoid(8)}`;
  const timestamp = Date.now();
  const cp        = input.curPrice;

  // Gate enforcement — may downgrade state AND posture
  const enforced = enforceGates(typed.thesis_state, typed.action_posture, gates, status);

  // Price validation — AI-supplied prices sanity-checked, replaced with null if implausible
  const entry_price_hint = validatePriceField(
    typed.entry_plan.entry_price_hint ?? null, cp, 'entry_price_hint');
  const target_1 = validatePriceField(
    typed.targets.target_1 ?? null, cp, 'target_1');
  const target_2 = validatePriceField(
    typed.targets.target_2 ?? null, cp, 'target_2');

  // what_changed comes from change_detection in the raw response
  const what_changed = typed.change_detection?.what_changed ?? [];

  const thesis: StructuredThesis = {
    thesis_id,
    symbol:    input.symbol,
    timeframe: input.timeframe,
    timestamp,

    thesis_state:   enforced.thesis_state,
    action_posture: enforced.action_posture,
    is_actionable:  enforced.thesis_state === 'ACTIONABLE',
    bias:           typed.bias,
    conviction:     typed.conviction,
    actionability:  typed.actionability,

    setup_classification: typed.setup_classification,
    setup_name:           typed.setup_name,
    summary:              typed.summary,
    decision:             typed.decision,

    entry_plan: {
      setup_type:          typed.setup_classification,  // derived
      entry_trigger:       typed.entry_plan.entry_trigger,
      entry_price_hint,
      confirmation_needed: typed.entry_plan.confirmation_needed,
      timing_note:         typed.entry_plan.timing_note ?? null,
    },

    risk_plan: {
      invalidation_level:  typed.risk_plan.invalidation_level,
      invalidation_reason: typed.risk_plan.invalidation_reason,
      stop_quality:        input.risk?.stopQuality   ?? 0,   // always from engine
      trap_conflict:       input.risk?.trapConflict  ?? false,
      regime_conflict:     input.risk?.regimeConflict ?? false,
      primary_risk_note:   typed.risk_plan.primary_risk_note,
    },

    targets: {
      target_1,
      target_2,
      risk_reward:  input.risk?.expectedR ?? null,           // always from engine
      target_logic: typed.targets.target_logic,
    },

    management: {
      max_hold_note:          typed.management.max_hold_note,
      scale_note:             typed.management.scale_note    ?? null,
      re_entry_note:          typed.management.re_entry_note ?? null,
      uncertainty_disclosure: typed.management.uncertainty_disclosure,
    },

    evidence: {
      smc_read:           typed.evidence.smc_read,
      regime_read:        typed.evidence.regime_read,
      liquidity_read:     typed.evidence.liquidity_read,
      trap_read:          typed.evidence.trap_read,
      gamma_read:         typed.evidence.gamma_read,
      setup_read:         typed.evidence.setup_read,
      supporting_factors: typed.evidence.supporting_factors,
      conflict_factors:   typed.evidence.conflict_factors,
      key_drivers:        typed.evidence.key_drivers,
      what_changed,
    },

    confidence,

    // Filled after full object is built
    change_detection: {
      changed:               false,
      change_type:           'NO_MATERIAL_CHANGE',
      material_changes:      [],
      previous_thesis_state: null,
      previous_posture:      null,
      previous_bias:         null,
      conviction_delta:      null,
    },

    audit: {
      thesis_id,
      generated_at:      timestamp,
      model_id:          modelId,
      source_version:    APP_VERSION,
      prompt_version:    PROMPT_VERSION,
      schema_version:    SCHEMA_VERSION,
      input_hash:        evidenceHash,
      gate_results:      gates.results,
      validation_status: enforced.status,
      fallback_reason:   null,
      generation_ms:     latencyMs,
      repair_attempts:   repairAttempts,
    },
  };

  // Change detection on the fully assembled object
  thesis.change_detection = detectThesisChanges(thesis, input.priorThesis);

  return thesis;
}

// ─── Main pipeline ────────────────────────────────────────────────────────────

export async function generateStructuredThesis(
  input:      ThesisEvidenceInput,
  aiCaller:   AICaller,
  gateConfig: GateConfig = DEFAULT_GATE_CONFIG,
): Promise<ThesisPipelineResult> {
  const start = Date.now();

  // 1. Deterministic gates
  const gates = evaluateDeterministicGates(input, gateConfig);

  // 2. Confidence (gate-aware)
  const confidence = scoreConfidence(input, gates);

  // 3. Normalized payload + stable hash
  const payload = buildNormalizedPayload(input);
  payload.gates_passed      = gates.passed;
  payload.gate_fail_reasons = gates.failures;
  payload.can_be_actionable = gates.passed;
  const evidenceHash = hashEvidence(JSON.stringify(payload));

  // 4. Messages (system + user separated for prompt caching)
  const aiMessages = buildAIMessages({ input, payload, gates, evidenceHash });

  // 5. AI call (tier 1)
  let rawText        = '';
  let usedModelId    = 'unknown';
  let repairAttempts = 0;

  try {
    const r = await aiCaller(aiMessages.system, aiMessages.messages);
    rawText     = r.text;
    usedModelId = r.modelId;
  } catch (err: any) {
    return { thesis: buildFallbackThesis(input, gates, confidence, evidenceHash,
      `AI call failed: ${err?.message ?? 'unknown'}`),
      validation_status: 'FALLBACK', gate_passed: gates.passed, latency_ms: Date.now() - start };
  }

  // 6. Parse JSON
  const parseResult = parseAIJSON(rawText);
  if (!parseResult.ok) {
    return { thesis: buildFallbackThesis(input, gates, confidence, evidenceHash,
      `JSON parse failed: ${parseResult.error}`),
      validation_status: 'FALLBACK', gate_passed: gates.passed, latency_ms: Date.now() - start };
  }

  // 7. Validate raw response → typed RawAIThesis
  let typedRaw: RawAIThesis | null = null;
  let finalStatus: ValidationStatus = 'AI_VALIDATED';

  const validation = validateRawAIResponse(parseResult.raw!);
  if (validation.ok) {
    typedRaw = validation.data;
  } else {
    // 8a. Model repair — ask the model to fix its own output
    repairAttempts = 1;
    let repairText: string | null = null;
    try {
      const repairMsgs = buildRepairMessages(
        aiMessages, rawText, validation.errors, input.symbol, input.curPrice);
      const repairResult = await aiCaller(repairMsgs.system, repairMsgs.messages);
      repairText  = repairResult.text;
      usedModelId = repairResult.modelId;
    } catch { /* fall through to deterministic repair */ }

    if (repairText) {
      const repairParse = parseAIJSON(repairText);
      if (repairParse.ok) {
        const repairValidation = validateRawAIResponse(repairParse.raw!);
        if (repairValidation.ok) {
          typedRaw    = repairValidation.data;
          finalStatus = 'AI_REPAIRED';
        }
      }
    }

    // 8b. Deterministic repair — coerce the raw object into a valid shape
    if (!typedRaw) {
      const repairedRaw  = repairFields(parseResult.raw!);
      const repairValn   = validateRawAIResponse(repairedRaw);
      if (!repairValn.ok) {
        return { thesis: buildFallbackThesis(input, gates, confidence, evidenceHash,
          `Validation failed after repair: ${repairValn.errors.join('; ')}`),
          validation_status: 'FALLBACK', gate_passed: gates.passed, latency_ms: Date.now() - start };
      }
      typedRaw    = repairValn.data;
      finalStatus = 'AI_REPAIRED';
    }
  }

  // 9. Assemble from typed object (no more generic access)
  const latencyMs = Date.now() - start;
  const thesis    = assembleThesis(
    typedRaw, input, confidence, gates,
    finalStatus, latencyMs, evidenceHash, usedModelId, repairAttempts);

  // 10. Final schema validation — typed AssembledThesis or fallback
  const finalValidation = validateFinalThesis(thesis);
  if (!finalValidation.ok) {
    return { thesis: buildFallbackThesis(input, gates, confidence, evidenceHash,
      `Final thesis schema failed: ${finalValidation.errors.slice(0, 3).join('; ')}`),
      validation_status: 'FALLBACK', gate_passed: gates.passed, latency_ms: Date.now() - start };
  }

  return {
    thesis:            finalValidation.data as StructuredThesis,
    validation_status: thesis.audit.validation_status,
    gate_passed:       gates.passed,
    latency_ms:        latencyMs,
  };
}
